import { useState, useEffect, useCallback } from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';
import debounce from 'lodash.debounce';

const LocationSearchBar = ({ 
  onSearchChange, 
  onFilterChange, 
  searchTerm = '', 
  filter = 'all',
  totalLocations = 0,
  filteredCount = 0,
  className = ''
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [showFilters, setShowFilters] = useState(false);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((term) => {
      onSearchChange?.(term);
    }, 200),
    [onSearchChange]
  );

  useEffect(() => {
    debouncedSearch(localSearchTerm);
    return () => debouncedSearch.cancel();
  }, [localSearchTerm, debouncedSearch]);

  const handleClear = () => {
    setLocalSearchTerm('');
    onSearchChange?.('');
  };

  const filterOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'active', label: 'Active Offices' },
    { value: 'planned', label: 'Planned Offices' },
    { value: 'countries', label: 'Countries Only' },
    { value: 'states', label: 'US States' },
    { value: 'provinces', label: 'CA Provinces' },
  ];

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Search Row */}
      <div className="flex items-center gap-3">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400" />
          <input
            type="text"
            placeholder={`Search ${totalLocations} locations...`}
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            className="w-full pl-11 pr-10 py-2.5 bg-white border border-gray-200 rounded-xl
                     text-sm text-gray-800 placeholder-gray-400
                     focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400
                     transition-all duration-200"
          />
          {localSearchTerm && (
            <button
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md
                       text-gray-400 hover:text-gray-600 hover:bg-gray-100
                       transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`
            inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium
            transition-all duration-200
            ${showFilters 
              ? 'bg-gray-800 text-white border-gray-800' 
              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }
          `}
        >
          <SlidersHorizontal className="w-4 h-4" />
          <span className="hidden sm:inline">Filters</span>
        </button>
      </div>

      {/* Filter Options */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 pt-1">
          {filterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => onFilterChange?.(option.value)}
              className={`
                px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200
                ${filter === option.value
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }
              `}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Results Counter */}
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">
          {filteredCount === totalLocations ? (
            <span>Showing all <span className="font-medium text-gray-700">{totalLocations}</span> locations</span>
          ) : (
            <span>
              Showing <span className="font-medium text-gray-700">{filteredCount}</span> of {totalLocations} locations
            </span>
          )}
        </span>
        
        {(searchTerm || filter !== 'all') && (
          <button
            onClick={() => {
              handleClear();
              onFilterChange?.('all');
            }}
            className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
};

export default LocationSearchBar;
